import { useMutation } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { useCallback } from 'react'
import { queryClient } from 'v2/configs/rq-setup'
import { flowService } from 'v2/shared/services/flow-service'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { flowQueryKeys } from 'v2/shared/utils/query-key-management'
import { errorToast, successToast } from 'v2/utils/toasts'
import { useFlowId } from '../hooks/use-flow-id'
import type { RKnowledgeBase } from '../knowledge-base/types'
import { useFlowStore } from '../store'
import type { FlowData } from '../types'

// Whether a KB is linked to the agent being built. An unsaved (?new) agent has no server-side links
// yet, so the ids staged in the flow store are the whole truth until its first save.
export const useIsKbLinked = () => {
  const { id = '', isNew } = useFlowId()
  const stagedIds = useFlowStore((state) => state.agentSettings?.knowledge_base_ids)
  return useCallback(
    (kb: RKnowledgeBase) => (isNew ? !!stagedIds?.includes(kb.id) : !!kb.phlo_uuids?.includes(id)),
    [id, isNew, stagedIds],
  )
}

export const useLinkKnowledgeBaseMutation = () => {
  const { isNew } = useFlowId()
  return useMutation({
    mutationFn: async ({
      knowledgeBaseIds,
      flowId,
    }: {
      knowledgeBaseIds: string[]
      flowId: string
    }) => {
      // No flow row exists before the first save (the POST would 404 "Flow not found"). onSuccess
      // stages the ids in the store + flowData cache, and the first save sends them.
      if (isNew) return
      await flowService.updateFlowSettings({ knowledge_base_ids: knowledgeBaseIds }, flowId)
    },
    onSuccess: (_, { knowledgeBaseIds, flowId }) => {
      successToast({ title: 'Success', description: 'Agent flow is updated with the selected KBs' })
      // Reflect the just-persisted link set in the cached flowData IMMEDIATELY. The flow publish prefers
      // flowData.phlo.knowledge_base_ids (the backend truth) — but the invalidate below only schedules a
      // refetch, so for one network round-trip flowData still holds the OLD list. A publish in that
      // window would replace-all with the stale list and drop a just-linked KB. This optimistic write
      // bridges the gap (the refetch then reconciles from the API).
      queryClient.setQueryData<FlowData>(flowQueryKeys().flowData(flowId), (old) =>
        old?.phlo ? { ...old, phlo: { ...old.phlo, knowledge_base_ids: knowledgeBaseIds } } : old,
      )
      // Keep the flow store in sync with EVERY link/unlink. create-kb-inline builds the new link set
      // from agentSettings.knowledge_base_ids; an UNLINK only updated the query cache (not the store),
      // so creating the next KB read a stale store and re-added the just-unlinked KB (the "unlinked KB
      // reappears" bug). Centralising it here covers unlink + link + create. setAgentSettings merges.
      useFlowStore.getState().setAgentSettings({ knowledge_base_ids: knowledgeBaseIds })
      // Base key invalidation (prefix-matches ['knowledge-bases', flowId] too) so BOTH the agent's
      // KB table and the org-wide useGetKnowledgeBases — which feeds the popover + unlink linkedIds
      // — refetch; otherwise an unlink leaves the popover/linked set stale.
      queryClient.invalidateQueries({ queryKey: flowQueryKeys().knowledgeBases })
      queryClient.invalidateQueries({ queryKey: flowQueryKeys().flowData(flowId) })
    },
    onError: (error: AxiosError) => {
      errorToast({
        title: 'Failed to link knowledge bases',
        description: getErrorText(error),
      })
    },
  })
}
